import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { publicApi } from "@/lib/api";

const FALLBACK_DARK_LOGO = "/assets/logos/bsc-alliance-dark-emblem.png";

const COL_1 = [
  { to: "/alliance", label: "Alliance" },
  { to: "/manifesto", label: "Manifesto" },
  { to: "/research", label: "Research" },
  { to: "/research-compendium", label: "Research Compendium" },
];
const COL_2 = [
  { to: "/beyond-science", label: "Beyond Science Series" },
  { to: "/founder", label: "Founder" },
  { to: "/founding-members", label: "Founding Members" },
  { to: "/programs", label: "Programs" },
];
const COL_3 = [
  { to: "/engage#join", label: "Join BSCA" },
  { to: "/engage#contribution", label: "Submit Contribution" },
  { to: "/media", label: "Media & Insights" },
  { to: "/engage#contact", label: "Contact" },
];

export default function Footer() {
  const [logo, setLogo] = useState(FALLBACK_DARK_LOGO);
  useEffect(() => {
    publicApi
      .siteSettings()
      .then((s) => {
        const url = s?.logo_assets?.dark_emblem || s?.logo_assets?.primary;
        if (url) setLogo(url);
      })
      .catch(() => {});
  }, []);
  return (
    <footer data-testid="site-footer" className="relative bsca-dark bsca-cosmic bsca-grain overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4 space-y-5">
            <div className="flex items-center gap-4">
              <img
                src={logo}
                alt="BSC Alliance — Science · Consciousness · Evolution"
                data-testid="footer-logo-img"
                className="h-14 w-auto object-contain"
              />
              <div className="leading-tight">
                <div className="font-serif text-lg">Beyond Science & Consciousness Alliance</div>
                <div className="bsca-eyebrow text-white/55">BSCA · bscalliance.org</div>
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed max-w-sm">
              A global alliance bridging empirical science, consciousness research, deep technology, philosophy, and wisdom traditions.
            </p>
            <p className="text-white/85 text-sm">
              <span className="bsca-eyebrow text-white/55 mr-2">Email</span>
              <a href="mailto:info@bscalliance.org" data-testid="footer-public-email" className="bsca-link text-[#D4AF37] hover:text-[#F0D060]">info@bscalliance.org</a>
            </p>
            <p className="bsca-eyebrow text-[#D4AF37]">Expanding Science · Awakening Consciousness</p>
          </div>

          <div className="md:col-span-2">
            <h4 className="bsca-eyebrow text-white/55 mb-4">Discover</h4>
            <ul className="space-y-2.5">
              {COL_1.map((i) => (
                <li key={i.to}>
                  <Link to={i.to} className="text-white/85 hover:text-[#D4AF37] text-sm bsca-link">
                    {i.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="bsca-eyebrow text-white/55 mb-4">Library & People</h4>
            <ul className="space-y-2.5">
              {COL_2.map((i) => (
                <li key={i.to}>
                  <Link to={i.to} className="text-white/85 hover:text-[#D4AF37] text-sm bsca-link">
                    {i.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="bsca-eyebrow text-white/55 mb-4">Engage</h4>
            <ul className="space-y-2.5">
              {COL_3.map((i) => (
                <li key={i.to}>
                  <Link to={i.to} className="text-white/85 hover:text-[#D4AF37] text-sm bsca-link inline-flex items-center gap-1">
                    {i.label} <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-white/55 text-xs">
          <div>
            © {new Date().getFullYear()} Science + Consciousness Alliance. A global research and consciousness ecosystem.
          </div>
          <div className="flex items-center gap-5">
            <Link to="/privacy-policy" data-testid="nav-link-privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms-of-use" data-testid="nav-link-terms" className="hover:text-white">Terms of Use</Link>
            <Link to="/admin" data-testid="nav-link-admin" className="hover:text-[#D4AF37]">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
